import type { TChartSeries, TTimeseriesData, TValueTypeObject } from 'components/StackedChart/types'
import type { TRGBAColorWith1AtTheEnd, TRGBAColorWithOpacityAtTheEnd } from 'settings/colors'
import { alphaColorReplacer } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import type { TUnitConverterFunction } from 'utils/unitConverter/unitConverter'
import { environmentalLineChartSeriesMaker } from './environmentalLineChartSeriesMaker'

export const generateSeriesForEnvironmentalChart = ({
  color,
  compareSeasonsColor,
  compareSeasonsValueTypeObject,
  hasSensor,
  unitConverter,
  valueTypeObject,
  overrideSeriesName,
}: {
  color?: TRGBAColorWith1AtTheEnd
  compareSeasonsColor?: TRGBAColorWith1AtTheEnd | TRGBAColorWithOpacityAtTheEnd
  compareSeasonsValueTypeObject?: TValueTypeObject
  hasSensor: boolean
  unitConverter: TUnitConverterFunction
  valueTypeObject?: TValueTypeObject
  overrideSeriesName?: string
}): TChartSeries[] => {
  const returnedSeries: TChartSeries[] = []

  if (!hasSensor) return []

  const commonEnvironmentalLineChartSeriesMakerParams = {
    aggregationInterval: valueTypeObject?.metadata?.aggregationInterval,
    /**
     * note that we should use the same name for regular and compare seasons
     * series, but that we can't rely on the name of a series for debugging,
     * and we should use the series' id when debugging instead
     */
    name: overrideSeriesName ?? unitConverter().shortTitle(),
    tooltip: {
      valueSuffix: ` ${unitConverter().suffix()}`,
      valueDecimals: unitConverter().defaultNumberOfDecimalPlaces(),
    },
  }

  const includeCompareSeasonsSeries = !!detailsPanelStore.getState().compareSeasonsInterval

  const commonValuesMapper = (ts: TTimeseriesData) => ({
    x: ts.timestamp,
    y: unitConverter(typeof ts.value === 'object' ? ts.value?.average : ts.value).value(),
    ...(!includeCompareSeasonsSeries && {
      low: unitConverter(typeof ts.value === 'object' ? ts.value?.min : null).value(),
      high: unitConverter(typeof ts.value === 'object' ? ts.value?.max : null).value(),
    }),
    isDeviceReported: ts.isDeviceReported,
  })

  const sensorSeries = environmentalLineChartSeriesMaker({
    ...commonEnvironmentalLineChartSeriesMakerParams,
    color,
    values: valueTypeObject?.timeseries?.map(commonValuesMapper) ?? [],
  })

  returnedSeries.push(...sensorSeries)

  if (includeCompareSeasonsSeries) {
    const colorObjectToUse = compareSeasonsColor
      ? { color: compareSeasonsColor }
      : color && { color: alphaColorReplacer(color, 0.4) }

    const compareSeasonsSeries = environmentalLineChartSeriesMaker({
      ...commonEnvironmentalLineChartSeriesMakerParams,
      // IMPORTANT: keep color in sync with makeCompareSeasonsSeriesFromRegularSeries
      ...colorObjectToUse,
      values: compareSeasonsValueTypeObject?.timeseries?.map(commonValuesMapper) ?? [],
    }).map((series, i) => ({
      ...series,
      // IMPORTANT: keep these in sync with makeCompareSeasonsSeriesFromRegularSeries
      id: `${sensorSeries[i].id}_compareSeasons`,
      fillOpacity: 0.3,
      hideFromTooltip: true,
      linkedTo: sensorSeries[0].id,
      showInLegend: false,
      zones: [],
    }))

    returnedSeries.push(...compareSeasonsSeries)
  }

  return returnedSeries
}
